// src/screens/ClientWelcome.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 26 Section 1 — Client Welcome Screen
// Full-screen, navy, personal. The first thing the client sees on screenshare.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../types/ClientData';

interface Props {
  data: ClientData;
  onBegin: () => void;
  sessionNumber: 1 | 2 | 3;
}

const sessionTitles: Record<1 | 2 | 3, string> = {
  1: 'Personal Plan',
  2: 'Business Engine',
  3: 'Life Design',
};

const sessionQuestions: Record<1 | 2 | 3, string> = {
  1: 'What does your life need to be financially free?',
  2: 'Can your business fund the life you want?',
  3: 'What do you want to do with your financial freedom?',
};

export default function ClientWelcome({ data, onBegin, sessionNumber }: Props) {
  const clientFirst = (data.clientName || '').split(' ')[0];
  const partnerFirst = (data.partnerName || '').split(' ')[0];
  const showCouple = data.isCouple && partnerFirst;
  const displayName = showCouple ? `${clientFirst} & ${partnerFirst}` : clientFirst;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: '#001F8C',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      {/* Motion Accountancy label */}
      <div
        style={{
          fontSize: 13,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: '#64DAD2',
          marginBottom: 20,
          fontWeight: 500,
        }}
      >
        Motion Accountancy
      </div>

      {/* Teal divider */}
      <div
        style={{
          width: 60,
          height: 2,
          backgroundColor: '#64DAD2',
          marginBottom: 28,
        }}
      />

      {/* Client name */}
      <div
        style={{
          fontSize: 48,
          fontWeight: 600,
          color: '#FFFFFF',
          marginBottom: 8,
        }}
      >
        {displayName}
      </div>

      {/* Subtitle */}
      <div
        style={{
          fontSize: 20,
          color: 'rgba(255, 255, 255, 0.7)',
          fontWeight: 400,
          marginBottom: 40,
        }}
      >
        Welcome to your {sessionTitles[sessionNumber]}
      </div>

      {/* Teal italic question */}
      <div
        style={{
          fontSize: 26,
          fontStyle: 'italic',
          color: '#64DAD2',
          textAlign: 'center',
          maxWidth: 600,
          lineHeight: 1.4,
          marginBottom: 60,
        }}
      >
        &ldquo;{sessionQuestions[sessionNumber]}&rdquo;
      </div>

      {/* Begin button */}
      <button
        onClick={onBegin}
        style={{
          width: 240,
          height: 56,
          borderRadius: 8,
          backgroundColor: '#64DAD2',
          color: '#001F8C',
          fontSize: 18,
          fontWeight: 600,
          border: 'none',
          cursor: 'pointer',
          fontFamily: "'Montserrat', sans-serif",
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
      >
        Let&rsquo;s begin →
      </button>
    </div>
  );
}
